/* ============================================================
   subscription-widget.js
   PDP subscription widget (2026 CI redesign).
   Custom element <subscription-widget> with two purchase types
   (one-time / recurring) and a custom delivery-frequency
   dropdown. Keeps the existing product-form / price integration
   (selling_plan hidden input, .price__subscription, unit price).
   ============================================================ */

class SubscriptionWidget extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId || null;

    this.purchaseRadios = Array.from(this.querySelectorAll(".abo-option__radio"));
    this.planRadios = Array.from(this.querySelectorAll("input.selling-plan-selector"));
    this.oneTimeOption = this.querySelector(".abo-option--onetime");
    this.subOption = this.querySelector(".abo-option--subscription");
    this.frequency = this.querySelector("[data-abo-frequency]");
    this.dropdown = this.querySelector("[data-abo-dropdown]");
    this.trigger = this.querySelector("[data-abo-dropdown-trigger]");
    this.currentLabel = this.querySelector("[data-abo-current-label]");
    this.currentBadge = this.querySelector("[data-abo-current-badge]");
    this.subPriceEl = this.querySelector("[data-abo-sub-price]");

    // Purchase type (Einmalig / Abonnieren)
    this.purchaseRadios.forEach((radio) =>
      radio.addEventListener("change", () => this.onPurchaseTypeChange())
    );

    // Delivery frequency options
    this.planRadios.forEach((radio) =>
      radio.addEventListener("change", (event) => this.onPlanChange(event))
    );

    // Dropdown open / close
    if (this.trigger) {
      this.trigger.addEventListener("click", (event) => {
        event.preventDefault();
        this.toggleDropdown();
      });
    }

    document.addEventListener("click", (event) => {
      if (!this.contains(event.target)) this.closeDropdown();
    });

    this.addEventListener("keydown", (event) => {
      if (event.key === "Escape") this.closeDropdown();
    });

    // Set the correct initial state (default = one-time unless configured)
    this.onPurchaseTypeChange(true);
    this.syncDropdownLabelWidth();

    this.handleViewportResize = () => this.syncDropdownLabelWidth();
    window.addEventListener("resize", this.handleViewportResize);

    if ("ResizeObserver" in window) {
      this.labelWidthObserver = new ResizeObserver(() => this.syncDropdownLabelWidth());
      this.labelWidthObserver.observe(this);
    }

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (this.isConnected) this.syncDropdownLabelWidth();
      });
    }
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.handleViewportResize);
    this.labelWidthObserver?.disconnect();
  }

  syncDropdownLabelWidth() {
    if (!this.dropdown) return;

    const labels = [
      this.currentLabel?.textContent,
      ...Array.from(this.querySelectorAll(".abo-dropdown__option-label"), (label) => label.textContent),
    ].map((label) => label?.trim()).filter(Boolean);

    if (!labels.length) return;

    const ruler = document.createElement("span");
    const labelStyle = window.getComputedStyle(this.currentLabel || this.querySelector(".abo-dropdown__option-label"));

    ruler.setAttribute("aria-hidden", "true");
    Object.assign(ruler.style, {
      position: "fixed",
      left: "-9999px",
      top: "0",
      display: "block",
      width: "max-content",
      visibility: "hidden",
      whiteSpace: "nowrap",
      font: labelStyle.font,
      letterSpacing: labelStyle.letterSpacing,
    });
    this.appendChild(ruler);

    let maxLabelWidth = 0;
    labels.forEach((label) => {
      ruler.textContent = label;
      maxLabelWidth = Math.max(maxLabelWidth, ruler.getBoundingClientRect().width);
    });

    ruler.remove();
    this.dropdown.style.setProperty("--abo-plan-label-width", `${Math.ceil(maxLabelWidth)}px`);
  }

  get selectedType() {
    const checked = this.querySelector(".abo-option__radio:checked");
    return checked ? checked.dataset.aboType : "one-time";
  }

  get selectedPlanId() {
    const checked = this.querySelector("input.selling-plan-selector:checked");
    return checked ? checked.value : null;
  }

  onPurchaseTypeChange() {
    const type = this.selectedType;

    if (this.oneTimeOption) this.oneTimeOption.classList.toggle("is-selected", type === "one-time");
    if (this.subOption) this.subOption.classList.toggle("is-selected", type === "recurring");

    if (type === "recurring") {
      let planId = this.selectedPlanId;
      if (!planId && this.planRadios.length) {
        this.planRadios[0].checked = true;
        planId = this.planRadios[0].value;
      }
      this.applyPlan(planId);
    } else {
      this.closeDropdown();
      this.setSellingPlansInProductForm("", this.productId);
      this.setSubscriptionPrice("");
      this.displayCorrectUnitPrice("");
    }
  }

  onPlanChange(event) {
    const planId = event.target.value;
    const option = event.target.closest(".abo-dropdown__option");
    this.updateTriggerFromOption(option);
    this.closeDropdown();

    // Selecting a delivery frequency implies the subscription option
    const subRadio = this.querySelector('.abo-option__radio[data-abo-type="recurring"]');
    if (subRadio && !subRadio.checked) {
      subRadio.checked = true;
      this.onPurchaseTypeChange();
      return;
    }

    this.applyPlan(planId);
  }

  applyPlan(planId) {
    if (!planId) return;

    this.setSellingPlansInProductForm(planId, this.productId);

    const input = this.querySelector(`input.selling-plan-selector[value="${planId}"]`);
    const option = input ? input.closest(".abo-dropdown__option") : null;

    if (option) {
      const subscriptionPrice = option.querySelector(".selling-plan-price")?.innerHTML || null;
      this.setSubscriptionPrice(subscriptionPrice);
      if (this.subPriceEl && option.dataset.planPrice) {
        this.subPriceEl.textContent = option.dataset.planPrice;
      }
      this.updateTriggerFromOption(option);
    }

    this.displayCorrectUnitPrice(planId);

    // Keep the selected plan hidden from the list (shown in the trigger)
    this.querySelectorAll(".abo-dropdown__option").forEach((opt) =>
      opt.classList.toggle("is-current", opt === option)
    );
  }

  updateTriggerFromOption(option) {
    if (!option) return;
    const label = option.querySelector(".abo-dropdown__option-label")?.textContent;
    const badge = option.querySelector(".abo-badge");

    if (this.currentLabel && label) this.currentLabel.textContent = label.trim();

    if (this.currentBadge) {
      if (badge) {
        this.currentBadge.textContent = badge.textContent;
        this.currentBadge.hidden = false;
      } else {
        this.currentBadge.hidden = true;
      }
    }
  }

  toggleDropdown() {
    if (!this.dropdown) return;
    if (this.dropdown.classList.contains("is-open")) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown() {
    if (!this.dropdown) return;
    this.dropdown.classList.add("is-open");
    this.trigger?.setAttribute("aria-expanded", "true");
  }

  closeDropdown() {
    if (!this.dropdown) return;
    this.dropdown.classList.remove("is-open");
    this.trigger?.setAttribute("aria-expanded", "false");
  }

  /* ---------------------------------------------------------
     Product-form / price integration (unchanged behaviour)
     --------------------------------------------------------- */
  setSellingPlansInProductForm(currentSelectedSellingPlanId, productId) {
    if (!productId) return;
    if (currentSelectedSellingPlanId == undefined) return;

    const allProductInputs = document.querySelectorAll(
      `input[name="product-id"][value="${productId}"]`
    );
    allProductInputs.forEach((currentProductInput) => {
      const formElement = currentProductInput.closest("form");
      if (!formElement) return;

      let sellingPlanInput = formElement.querySelector("input[name='selling_plan']");

      if (currentSelectedSellingPlanId) {
        if (sellingPlanInput) {
          sellingPlanInput.value = currentSelectedSellingPlanId;
        } else {
          sellingPlanInput = document.createElement("input");
          sellingPlanInput.type = "hidden";
          sellingPlanInput.name = "selling_plan";
          sellingPlanInput.value = currentSelectedSellingPlanId;
          formElement.appendChild(sellingPlanInput);
        }
      } else if (sellingPlanInput) {
        sellingPlanInput.value = "";
      }
    });
  }

  setSubscriptionPrice(newProductPrice) {
    const priceElement = document.querySelector(".price__subscription");
    const priceWrapper = document.querySelector(".price__wrapper");

    if (newProductPrice != "") {
      if (priceWrapper && priceElement) {
        const priceInnerWrapper = priceElement.querySelector(".subscription-price");
        if (priceInnerWrapper) {
          priceElement.style.display = "block";
          priceWrapper.classList.add("active");
          priceInnerWrapper.innerHTML = newProductPrice;
        }
        priceWrapper.classList.add("subscription-price");
      }
    } else if (priceWrapper && priceElement) {
      priceElement.style.display = "none";
      priceWrapper.classList.remove("active");
      priceWrapper.classList.remove("subscription-price");
    }
  }

  displayCorrectUnitPrice(currentSellingPlan) {
    const unitPriceWrapper = document.querySelector(".unit-tax-wrapper");
    if (!unitPriceWrapper) return;

    const findSellingPlanElement = unitPriceWrapper.querySelectorAll("[data-selling-plan-id]");
    if (findSellingPlanElement) {
      findSellingPlanElement.forEach((ele) => {
        if (ele.dataset.sellingPlanId.trim() === currentSellingPlan.trim()) {
          ele.style.display = "block";
        } else {
          ele.style.display = "none";
        }
      });
    }
  }
}

if (!customElements.get("subscription-widget")) {
  customElements.define("subscription-widget", SubscriptionWidget);
}
